import {
  coarseSeed,
  decayTraits,
  initialTraits,
  reduceTraits,
  type InputSample,
  type InputTraits,
} from './traits';

/**
 * **输入画像的 DOM 接线层。**
 *
 * 只做三件事：把浏览器事件摘成 `InputSample`、累进 `traits.ts` 的纯函数、
 * 每个 tick 做一次衰减 + 手柄轮询。所有判断逻辑都在 `traits.ts` 里。
 */
export interface InputProfile {
  /** 当前快照（读的时候不衰减；衰减在 `tick` 里做） */
  traits(): InputTraits;
  /** 每帧调一次：清易失位 + 轮询手柄 */
  tick(now: number): void;
  destroy(): void;
}

/** 纯修饰键：单独按下不算“在用键盘” */
const MODIFIER_KEYS = new Set([
  'Shift',
  'Control',
  'Alt',
  'Meta',
  'CapsLock',
  'NumLock',
  'ScrollLock',
  'Dead',
  'Process',
  'Unidentified',
]);

const COARSE_QUERY = '(hover: none) and (pointer: coarse)';

function matchesCoarse(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(COARSE_QUERY).matches
      : false;
  } catch {
    return false;
  }
}

/** 造一个输入画像。`target` 默认是 `window`，测试/嵌入时可以换 */
export function createInputProfile(target: Window = window): InputProfile {
  let current = matchesCoarse() ? coarseSeed() : initialTraits();

  const onPointer = (event: PointerEvent): void => {
    const sample: InputSample = {
      kind: 'pointer',
      at: event.timeStamp,
      pointer: {
        pointerType: event.pointerType,
        width: event.width,
        height: event.height,
        pressure: event.pressure,
        buttons: event.buttons,
      },
    };
    current = reduceTraits(current, sample);
  };

  const onKey = (event: KeyboardEvent): void => {
    current = reduceTraits(current, {
      kind: 'key',
      at: event.timeStamp,
      modifierOnly: MODIFIER_KEYS.has(event.key),
    });
  };

  const onGamepad = (event: GamepadEvent): void => {
    // 只是“连上了”——闩住 gamepadSeen，设置页据此决定要不要显示手柄一栏
    current = reduceTraits(current, { kind: 'gamepad-connected', at: event.timeStamp });
  };

  // capture=true：控件自己 stopPropagation 也挡不住画像更新
  target.addEventListener('pointerover', onPointer, true);
  target.addEventListener('pointerdown', onPointer, true);
  target.addEventListener('pointermove', onPointer, true);
  target.addEventListener('pointerup', onPointer, true);
  target.addEventListener('keydown', onKey, true);
  target.addEventListener('gamepadconnected', onGamepad, true);

  return {
    traits: () => current,
    tick(now: number): void {
      current = decayTraits(current, now);
      // 手柄没有“移动”事件可听，只能轮询：
      // - 看到任意一个连着的手柄 → 闩住 gamepadSeen（设置页据此显示手柄一栏）
      // - 有键按下才算“最近用了手柄”（易失位）
      if (typeof navigator !== 'undefined' && typeof navigator.getGamepads === 'function') {
        for (const pad of navigator.getGamepads()) {
          if (pad === null || !pad.connected) continue;
          current = reduceTraits(current, { kind: 'gamepad-connected', at: now });
          if (pad.buttons.some((button) => button.pressed)) {
            current = reduceTraits(current, { kind: 'gamepad', at: now });
          }
          break;
        }
      }
    },
    destroy(): void {
      target.removeEventListener('pointerover', onPointer, true);
      target.removeEventListener('pointerdown', onPointer, true);
      target.removeEventListener('pointermove', onPointer, true);
      target.removeEventListener('pointerup', onPointer, true);
      target.removeEventListener('keydown', onKey, true);
      target.removeEventListener('gamepadconnected', onGamepad, true);
    },
  };
}
